import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar } from '@mantine/core'
import { UserCheck, Users, Briefcase, DollarSign, TrendingUp } from 'react-feather'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import { getDashboardStats } from './helper'
import { formatDate } from '../../helpers/dateUtils'

const getInitials = (name?: string) => {
  if (!name) return ''
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

const avatarColors = ['#3882F6', '#0B1D3A', '#132D5E', '#0EA5E9']

const formatCurrency = (n: number) => '$' + Math.round(n || 0).toLocaleString()

const formatMonth = (iso: string) => new Intl.DateTimeFormat('en-US', { month: 'short' }).format(new Date(iso))

type StatTileProps = {
  label: string
  value: React.ReactNode
  sub?: string
  color: string
  tint: string
  icon: React.ReactNode
}

const StatTile = ({ label, value, sub, color, tint, icon }: StatTileProps) => (
  <div className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-500">{label}</span>
      <span
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: tint, color }}
      >
        {icon}
      </span>
    </div>
    <span className="text-3xl font-extrabold text-[#0B1D3A] leading-none">{value}</span>
    {sub && <span className="text-xs text-gray-400">{sub}</span>}
  </div>
)

const RecentList = ({ title, users }: { title: string; users: any[] }) => (
  <div className="bg-white rounded-xl shadow-md p-6">
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-base font-bold">{title}</span>
    </div>
    {users.length === 0 && <p className="text-gray-500 mt-3">No records yet</p>}
    <div className="flex flex-col">
      {users.map((u, idx) => (
        <Link
          key={u.uuid}
          to={`/all-users/${u.uuid}`}
          className="flex items-center gap-3 py-3 border-b last:border-b-0 no-underline text-inherit"
        >
          <Avatar
            src={u.profilePic}
            radius={36}
            size={36}
            styles={{ placeholder: { backgroundColor: avatarColors[idx % avatarColors.length], color: '#fff', fontWeight: 700, fontSize: 12 } }}
          >
            {getInitials(u.displayName)}
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold truncate">{u.displayName}</p>
            <p className="text-xs text-gray-400 truncate">{u.email}</p>
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(u.createdAt)}</span>
        </Link>
      ))}
    </div>
  </div>
)

export default function DashboardPage() {
  const [stats, setStats] = useState<any>()

  useEffect(() => {
    getDashboardStats().then((res) => setStats(res.data))
  }, [])

  const trend: any[] = stats?.earnings?.trend || []
  const totalIndividuals = (stats?.totalCoaches || 0) + (stats?.totalParticipants || 0)
  const coachPct = totalIndividuals ? Math.round((stats.totalCoaches / totalIndividuals) * 100) : 0
  const participantPct = totalIndividuals ? 100 - coachPct : 0

  const chartW = 640
  const chartH = 180
  const chartPad = 24
  const values = trend.map((t) => t.total)
  const maxVal = Math.max(...values, 1)
  const points = trend.map((t, i) => {
    const x =
      trend.length > 1
        ? chartPad + (i * (chartW - chartPad * 2)) / (trend.length - 1)
        : chartW / 2
    const y = chartH - 10 - (t.total / maxVal) * (chartH - 30)
    return { x, y, month: formatMonth(t.month), value: t.total }
  })
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const linePoints = points.map((p) => `${p.x},${p.y}`).join(' ')
  const areaPath =
    points.length > 0
      ? `M${points[0].x},${chartH} L${linePoints.replace(/ /g, ' L')} L${points[points.length - 1].x},${chartH} Z`
      : ''

  return (
    <DashboardWrapper page="dashboard" title="Dashboard">
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-4 gap-4">
          <StatTile
            label="Coaches"
            value={stats ? stats.totalCoaches : '—'}
            color="#3882F6"
            tint="#EAF1FE"
            icon={<UserCheck size={18} />}
          />
          <StatTile
            label="Participants"
            value={stats ? stats.totalParticipants : '—'}
            color="#B45309"
            tint="#FEF7E6"
            icon={<Users size={18} />}
          />
          <StatTile
            label="Organizations"
            value={stats ? stats.totalOrganizations : '—'}
            color="#0EA5E9"
            tint="#E6F6FD"
            icon={<Briefcase size={18} />}
          />
          <StatTile
            label="Total earnings"
            value={stats ? formatCurrency(stats.earnings.totalEarnings) : '—'}
            sub={
              stats
                ? `${formatCurrency(stats.earnings.paidTotal)} paid · ${formatCurrency(stats.earnings.pendingTotal)} pending`
                : undefined
            }
            color="#16A34A"
            tint="#EAF7EF"
            icon={<DollarSign size={18} />}
          />
        </div>

        <div className="grid gap-5 items-stretch" style={{ gridTemplateColumns: '1.6fr 1fr' }}>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <p className="text-base font-bold">Earnings trend</p>
                <p className="text-xs text-gray-400">Paid invoices, last 6 months</p>
              </div>
              {hoverIdx !== null && (
                <p className="text-sm">
                  {points[hoverIdx].month}: <span className="font-bold">{formatCurrency(points[hoverIdx].value)}</span>
                </p>
              )}
            </div>

            {points.length === 0 ? (
              <p className="text-gray-500 flex items-center gap-2">
                <TrendingUp size={16} /> No earnings recorded yet
              </p>
            ) : (
              <svg width="100%" height="200" viewBox={`0 0 ${chartW} ${chartH + 20}`} preserveAspectRatio="none">
                <line x1="0" y1={chartH - 10} x2={chartW} y2={chartH - 10} stroke="#E4E9F2" strokeWidth={1} />
                <line x1="0" y1={(chartH - 10) / 2} x2={chartW} y2={(chartH - 10) / 2} stroke="#E4E9F2" strokeWidth={1} />
                <path d={areaPath} fill="#3882F6" opacity="0.1" />
                <polyline points={linePoints} fill="none" stroke="#3882F6" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                {points.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={i === points.length - 1 ? 5 : 4}
                    fill="#3882F6"
                    stroke="#fff"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx(null)}
                  />
                ))}
                {points.map((p, i) => (
                  <text key={i} x={p.x} y={chartH + 14} fontSize="11" fill="#94A3C0" textAnchor="middle">
                    {p.month}
                  </text>
                ))}
              </svg>
            )}
          </div>

          <div className="flex flex-col gap-5">
            <div className="bg-white rounded-xl shadow-md p-6">
              <p className="text-base font-bold mb-3">Coaches vs participants</p>
              <div className="flex h-8 rounded-lg overflow-hidden">
                {coachPct > 0 && (
                  <div
                    style={{ width: `${coachPct}%`, background: '#3882F6' }}
                    className="flex items-center px-2 text-white text-xs font-bold"
                  >
                    {coachPct}%
                  </div>
                )}
                {participantPct > 0 && (
                  <div
                    style={{ width: `${participantPct}%`, background: '#FBBF24' }}
                    className="flex items-center px-2 text-[#0B1D3A] text-xs font-bold"
                  >
                    {participantPct}%
                  </div>
                )}
              </div>
              <div className="flex gap-5 mt-3 text-xs text-gray-500">
                <span>
                  <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: '#3882F6' }} />
                  <b className="text-[#0B1D3A]">{stats?.totalCoaches ?? 0}</b> coaches
                </span>
                <span>
                  <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: '#FBBF24' }} />
                  <b className="text-[#0B1D3A]">{stats?.totalParticipants ?? 0}</b> participants
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <p className="text-base font-bold mb-3">Needs attention</p>
              <Link
                to="/budget-request"
                className="flex items-center justify-between py-2 border-b no-underline text-inherit"
              >
                <span className="text-sm">Pending budget requests</span>
                <span className="text-xs font-bold bg-[#FEF7E6] text-[#0B1D3A] px-2.5 py-1 rounded-full">
                  {stats?.needsAttention?.pendingBudgetRequests ?? '—'}
                </span>
              </Link>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm">Certificates awaiting approval</span>
                <span className="text-xs font-bold bg-[#FEF7E6] text-[#0B1D3A] px-2.5 py-1 rounded-full">
                  {stats?.needsAttention?.pendingCertificates ?? '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-5">
          <RecentList title="Recent coaches" users={stats?.recentCoaches || []} />
          <RecentList title="Recent participants" users={stats?.recentParticipants || []} />
        </div>
      </div>
    </DashboardWrapper>
  )
}
